// src/components/forms/VendorForm.tsx — contractors, their CIS status and their tickets.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useEntityOptions } from '../../lib/options';
import { Button, Field, Input, Modal, Select } from '../ui';
import { FormError, FormGrid, Wide, nulled, numberOr } from './common';

type V = Record<string, any>;
type Cert = { name: string; number: string; issuedOn: string; expiresOn: string };
const TRADES = ['GAS', 'ELECTRICAL', 'PLUMBING', 'LIFT', 'FIRE', 'DOORS_WINDOWS', 'DAMP', 'PEST', 'CLEANING', 'GROUNDS', 'GENERAL'];
const CIS = [['NOT_APPLICABLE', 'Not applicable'], ['GROSS', 'Gross — no deduction'], ['NET_20', 'Net 20% — verified'], ['NET_30', 'Net 30% — unverified']];
const label = (s: string) => s.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

/**
 * Two things here are not decoration. CIS status decides how much tax is withheld
 * from a subcontractor's invoice, and the certifications are what makes a Gas Safe
 * or NICEIC engineer legal to send — an expired ticket is a compliance failure, so
 * the expiry dates are structured rather than a note.
 */
export function VendorForm({ open, onClose, vendor }: { open: boolean; onClose: () => void; vendor?: V }) {
  const isNew = !vendor;
  const qc = useQueryClient();
  const entities = useEntityOptions();
  const [form, setForm] = useState(() => ({
    name: vendor?.name ?? '', trade: vendor?.trade ?? 'GENERAL', email: vendor?.email ?? '', phone: vendor?.phone ?? '',
    companyNumber: vendor?.companyNumber ?? '', taxNumber: vendor?.taxNumber ?? '',
    cisStatus: vendor?.cisStatus ?? 'NOT_APPLICABLE', cisVerificationNumber: vendor?.cisVerificationNumber ?? '',
    rating: vendor?.rating == null ? '' : String(vendor.rating),
    isApproved: vendor?.isApproved ?? false, isActive: vendor?.isActive ?? true,
    region: vendor?.region ?? 'UK', entityId: vendor?.entityId ?? '',
  }));
  const [certs, setCerts] = useState<Cert[]>(() => (vendor?.certifications ?? []).map((c: Record<string, string>) => ({
    name: c.name ?? '', number: c.number ?? '', issuedOn: c.issuedOn ?? '', expiresOn: c.expiresOn ?? '',
  })));
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const body = () => ({
    name: form.name.trim(), trade: nulled(form.trade), email: nulled(form.email), phone: nulled(form.phone),
    companyNumber: nulled(form.companyNumber), taxNumber: nulled(form.taxNumber),
    cisStatus: form.cisStatus, cisVerificationNumber: nulled(form.cisVerificationNumber),
    certifications: certs.filter((c) => c.name.trim()).map((c) => ({
      name: c.name.trim(), number: nulled(c.number), issuedOn: nulled(c.issuedOn), expiresOn: nulled(c.expiresOn),
    })),
    rating: form.rating === '' ? null : numberOr(form.rating),
    isApproved: form.isApproved, isActive: form.isActive,
    region: form.region, entityId: nulled(form.entityId),
  });

  const save = useMutation({
    mutationFn: async () => (isNew ? api.post<V>('/vendors', body()) : api.patch<V>(`/vendors/${vendor!.id}`, body())),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['/vendors'] }); void qc.invalidateQueries({ queryKey: ['options', '/vendors'] }); onClose(); },
  });

  return (
    <Modal open={open} onClose={onClose} size="lg" title={isNew ? 'New vendor' : `Edit ${vendor!.name}`}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => save.mutate()} disabled={form.name.trim().length < 2 || save.isPending}>{save.isPending ? 'Saving…' : isNew ? 'Create vendor' : 'Save changes'}</Button></>}>
      <div className="space-y-5">
        <FormGrid>
          <Field label="Name"><Input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Thameside Gas Ltd" className="w-full" /></Field>
          <Field label="Trade"><Select value={form.trade} onChange={(e) => set({ trade: e.target.value })} className="w-full">{TRADES.map((t) => <option key={t} value={t}>{label(t)}</option>)}</Select></Field>

          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} className="w-full" /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => set({ phone: e.target.value })} className="w-full" /></Field>

          <Field label="Company number"><Input value={form.companyNumber} onChange={(e) => set({ companyNumber: e.target.value })} className="w-full" /></Field>
          <Field label="VAT / tax number"><Input value={form.taxNumber} onChange={(e) => set({ taxNumber: e.target.value })} className="w-full" /></Field>

          <Field label="CIS status" hint="Decides the deduction taken from their invoices"><Select value={form.cisStatus} onChange={(e) => set({ cisStatus: e.target.value })} className="w-full">{CIS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></Field>
          <Field label="CIS verification number"><Input value={form.cisVerificationNumber} onChange={(e) => set({ cisVerificationNumber: e.target.value })} disabled={form.cisStatus === 'NOT_APPLICABLE'} className="w-full" /></Field>

          <Field label="Region"><Select value={form.region} onChange={(e) => set({ region: e.target.value, entityId: '' })} className="w-full"><option value="UK">United Kingdom</option><option value="AE">United Arab Emirates</option></Select></Field>
          <Field label="Rating" hint="0–5, updated by performance"><Input value={form.rating} onChange={(e) => set({ rating: e.target.value })} inputMode="decimal" className="w-full" /></Field>

          <Wide><Field label="Company" hint="Leave unassigned to use across the group"><Select value={form.entityId} onChange={(e) => set({ entityId: e.target.value })} className="w-full"><option value="">Unassigned</option>{(entities.data ?? []).filter((e) => e.region === form.region).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</Select></Field></Wide>
        </FormGrid>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[12px] font-medium text-ink-600">Certifications <span className="text-ink-400">· Gas Safe, NICEIC, insurance</span></span>
            <button type="button" onClick={() => setCerts((c) => [...c, { name: '', number: '', issuedOn: '', expiresOn: '' }])} className="inline-flex items-center gap-1 text-[12px] font-medium text-bronze-600 hover:underline"><Plus className="h-3.5 w-3.5" /> Add</button>
          </div>
          {certs.length === 0 ? <p className="rounded-xl border border-dashed border-ink-200 py-4 text-center text-[12px] text-ink-400">None recorded</p> : (
            <div className="space-y-2">
              {certs.map((c, i) => (
                <div key={i} className="flex flex-wrap items-end gap-2 rounded-xl border border-ink-200 p-2.5">
                  <Input value={c.name} onChange={(e) => setCerts((x) => x.map((y, j) => j === i ? { ...y, name: e.target.value } : y))} placeholder="Gas Safe" className="w-36 !py-1.5 !text-[12px]" />
                  <Input value={c.number} onChange={(e) => setCerts((x) => x.map((y, j) => j === i ? { ...y, number: e.target.value } : y))} placeholder="Number" className="w-32 !py-1.5 !text-[12px]" />
                  <Input type="date" value={c.issuedOn} onChange={(e) => setCerts((x) => x.map((y, j) => j === i ? { ...y, issuedOn: e.target.value } : y))} className="w-36 !py-1.5 !text-[12px]" />
                  <Input type="date" value={c.expiresOn} onChange={(e) => setCerts((x) => x.map((y, j) => j === i ? { ...y, expiresOn: e.target.value } : y))} className="w-36 !py-1.5 !text-[12px]" />
                  <button type="button" onClick={() => setCerts((x) => x.filter((_, j) => j !== i))} className="ml-auto rounded-full p-1.5 text-ink-300 transition hover:bg-red-50 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-5">
          <label className="flex items-center gap-2.5 text-[13px] text-ink-700"><input type="checkbox" checked={form.isApproved} onChange={(e) => set({ isApproved: e.target.checked })} className="h-4 w-4 accent-ink-900" /> Approved to receive work</label>
          <label className="flex items-center gap-2.5 text-[13px] text-ink-700"><input type="checkbox" checked={form.isActive} onChange={(e) => set({ isActive: e.target.checked })} className="h-4 w-4 accent-ink-900" /> Active</label>
        </div>

        <FormError error={save.error} />
      </div>
    </Modal>
  );
}
